/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { AiOutlineLink } from "react-icons/ai";
import { Tooltip, IconButton } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdOutlineCancel, MdOutlineContentCopy } from "react-icons/md";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { TbRefreshAlert } from "react-icons/tb";
import PayoutCancelConfirmModal from "../../shared/PayoutCancelConfirmModal";
import ReceiverAccountDetailsModal from "../../shared/ReceiverAccountDetailsModal";
import PayoutMarkFailedConfirmModal from "./PayoutMarkFailedConfirmModal";
import { ProfileContext } from "../../../../store/ProfileProvider";
import DisplayAmount from "../../shared/DisplayAmount";
import AgGridPagination from "../../shared/AgGridPagination";
import SweetAlert2 from "react-sweetalert2";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type Receiver = {
  id: string;
  firs_tname: string;
  last_name: string;
  email: string;
};

type ReceiverAccount = {
  id: string;
  email: string;
  method_details: Object;
};

type PayoutProvider = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  status: string;
  amount: string;
  asset: string;
  created_at: string;
  note: string;
  merchant: Merchant;
  subaccount: Subaccount;
  transactions: any[];
  receiver: Receiver;
  receiver_account: ReceiverAccount;
  payout_provider: PayoutProvider;
  batch_id: string;
  cause: any;
  reference_tracking: any;
  method_name: string;
  crypto: boolean;
};

const PayoutList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadPayoutList(): void;
  processingBatchIds?: Set<string>;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadPayoutList,
  processingBatchIds,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [swalProps, setSwalProps] = useState<any>({});
  const [payoutToCancel, setPayoutToCancel] = useState<any>(null);
  const [isOpenPayoutCancelConfirmModal, setIsOpenPayoutCancelConfirmModal] =
    useState<boolean>(false);

  const [
    isOpenReceiverAccountDetailModal,
    setIsOpenReceiverAccountDetailModal,
  ] = useState<boolean>(false);
  const [receiverAccountDetailToDisplay, setReceiverAccountDetailToDisplay] =
    useState<any>(null);

  const [payoutToMarkFailed, setPayoutToMarkFailed] = useState<any>(null);
  const [
    isOpenPayoutMarkFailedConfirmModal,
    setIsOpenPayoutMarkFailedConfirmModal,
  ] = useState<boolean>(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const subaccountType = localStorage.getItem("subaccountType");

  interface PayoutStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: PayoutStatusItems[] = [
    {
      status: "awaiting",
      value: "awaiting",
      display: (
        <Badge className={`${statusClasses} status-awaiting`}>Awaiting</Badge>
      ),
    },
    {
      status: "sent_mail",
      value: "sent_mail",
      display: (
        <Badge className={`${statusClasses} status-sent_mail`}>
          Email Sent
        </Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "unassessed",
      value: "unassessed",
      display: (
        <Badge className={`${statusClasses} status-unassessed`}>
          Unassessed
        </Badge>
      ),
    },
    {
      status: "in_progress",
      value: "in_progress",
      display: (
        <Badge className={`${statusClasses} status-in_progress`}>
          In Progress
        </Badge>
      ),
    },
    {
      status: "successful",
      value: "successful",
      display: (
        <Badge className={`${statusClasses} status-successful`}>
          Completed
        </Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
    {
      status: "successful_pending_kyc",
      value: "successful_pending_kyc",
      display: (
        <Badge className={`${statusClasses} status-successful_pending_kyc`}>
          Successful Pending KYC
        </Badge>
      ),
    },
  ];

  const handleClickCancel = (payout: any) => {
    setPayoutToCancel(payout);
    setIsOpenPayoutCancelConfirmModal(true);
  };

  const closePayoutCancelConfirmModal = () => {
    setPayoutToCancel(null);
    setIsOpenPayoutCancelConfirmModal(false);
  };

  const handleAccountClickDetails = (payoutRecord: any) => {
    const accountDetail = {
      id: payoutRecord.receiver_account?.id,
      asset: payoutRecord.asset,
      method_name: payoutRecord.method_name,
      method_details: payoutRecord.receiver_account?.method_details,
      receiver_id: payoutRecord.receiver?.id,
    };
    setReceiverAccountDetailToDisplay(accountDetail);
    setIsOpenReceiverAccountDetailModal(true);
  };

  const closeAccountDetailsModal = () => {
    setReceiverAccountDetailToDisplay(null);
    setIsOpenReceiverAccountDetailModal(false);
  };

  const handleMarkFailed = (payout: any) => {
    setPayoutToMarkFailed(payout);
    setIsOpenPayoutMarkFailedConfirmModal(true);
  };

  const closePayoutMarkFailedConfirmModal = () => {
    setPayoutToMarkFailed(null);
    setIsOpenPayoutMarkFailedConfirmModal(false);
  };

  const handleClickCopy = async (payoutId: string) => {
    try {
      await navigator.clipboard.writeText(payoutId);
    } catch (err) {
      console.error("Failed to copy payout ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy Payout ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-payoutid-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const BatchRenderer = (params: ICellRendererParams) => {
    if (params.data.batch_id && params.data.batch_id !== "") {
      return (
        <Tooltip title="View Batch Details" arrow placement="right">
          <div>
            <IconButton
              component="a"
              href={`/${userRole}/batch/${params.data.batch_id}`}
              target="_blank"
              rel="noopener noreferrer"
              id={"button-view-batch-" + params.data.batch_id}
              size="small"
            >
              <AiOutlineLink className="flex h-5 w-5 justify-center" />
            </IconButton>
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const AmountRenderer = (params: ICellRendererParams) =>
    params.data.crypto ? (
      <>{params.data.amount}</>
    ) : (
      <DisplayAmount
        amount={Number(params.data.amount)}
        currency={params.data.asset}
      />
    );

  const AccountDetailRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Account Details" arrow placement="right">
      <IconButton
        id={"button-details-" + params.data?.id}
        onClick={() => handleAccountClickDetails(params.data)}
        size="small"
      >
        <AiOutlineInfoCircle className="h-5 w-5 text-navy-700" />
      </IconButton>
    </Tooltip>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    const failMessage =
      params.data.cause?.fail?.message ||
      (typeof params.data.cause?.fail === "string" ? params.data.cause.fail : null);
    if (
      userRole === "superadmin" &&
      status === "failed" &&
      failMessage
    ) {
      return (
        <Tooltip title={failMessage} arrow placement="top">
          <div className="cursor-help">
            {statusItem ? statusItem.display : null}
          </div>
        </Tooltip>
      );
    }
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const { status, batch_status, file_bucket, file_key } =
      params.data || {};

    const batchIsProcessing = params.data?.batch_id && processingBatchIds?.has(params.data.batch_id);
    const batchAllowsCancel =
      !batchIsProcessing && (!batch_status || ["open", "close"].includes(batch_status));

    const canCancel =
      userPermissions?.cancelPayout === true &&
      batchAllowsCancel &&
      (status === "awaiting" ||
        (userRole === "superadmin" &&
          status === "pending" &&
          ["open", "close"].includes(batch_status) &&
          file_bucket === null &&
          file_key === null) ||
        (status === "pending" && batch_status === "open"));

    const canMarkFailed =
      userRole === "superadmin" && status === "successful";

    return (
      <>
        {canCancel && (
          <Tooltip title="Mark as Cancel" arrow placement="top">
            <div>
              <IconButton
                id={`button-cancel-${params.data?.id}`}
                onClick={() => handleClickCancel(params.data)}
                size="small"
              >
                <MdOutlineCancel className="status-cancelled h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        {canMarkFailed && (
          <Tooltip title="Mark as Failed" arrow placement="top">
            <div>
              <IconButton
                id={`button-failed-${params.data?.id}`}
                onClick={() => handleMarkFailed(params.data)}
                size="small"
              >
                <TbRefreshAlert className="h-5 w-5 text-navy-700" />
              </IconButton>
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 160,
      flex: 1,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "subaccount",
      headerName: "",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "batch_id",
      headerName: "Batch",
      sortable: false,
      width: 70,
      cellRenderer: BatchRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "amount",
      headerName: "Amount",
      sortable: false,
      width: 90,
      cellRenderer: AmountRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "asset",
      headerName: "Asset",
      width: 70,
      sortable: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "receiver",
      headerName: "Email",
      sortable: false,
      minWidth: 200,
      flex: 1.5,
      valueGetter: (params: any) => params.data?.receiver?.email,
    },
    {
      field: "account_detail",
      headerName: "Account",
      sortable: false,
      width: 80,
      cellRenderer: AccountDetailRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "note",
      headerName: "Note",
      sortable: false,
      minWidth: 120,
      flex: 1,
    },
    {
      field: "reference",
      headerName: "Reference",
      sortable: false,
      minWidth: 120,
      flex: 1,
    },
    {
      field: "reference_tracking",
      headerName: "Ref. Tracking",
      sortable: false,
      minWidth: 140,
      flex: 1,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 150,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 80,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const filterColumns = useCallback(() => {
    let rebuildColumns = [...columns];
    const isMerchant = userRole === "merchant";
    if (userRole !== "superadmin") {
      const excludeFields: string[] = [];
      if (!userPermissions?.cancelPayout && userRole !== "superadmin") {
        excludeFields.push("actions");
      }
      if (!isMerchant) {
        excludeFields.push("subaccount");
      }
      if (subaccountType === "agent") {
        excludeFields.push("batch_id");
      }
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    }
    return rebuildColumns;
  }, [userRole, userPermissions, subaccountType, processingBatchIds]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <>
      {isOpenPayoutCancelConfirmModal &&
        (userRole === "superadmin" ||
          userRole === "admin" ||
          userRole === "subaccount" ||
          userRole === "merchant") &&
        payoutToCancel && (
          <PayoutCancelConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadPayoutList}
            isOpen={isOpenPayoutCancelConfirmModal}
            payoutToCancel={payoutToCancel}
            onClose={closePayoutCancelConfirmModal}
          />
        )}
      {isOpenReceiverAccountDetailModal && receiverAccountDetailToDisplay && (
        <ReceiverAccountDetailsModal
          detail={receiverAccountDetailToDisplay}
          isOpen={isOpenReceiverAccountDetailModal}
          onClose={closeAccountDetailsModal}
          receiverId={receiverAccountDetailToDisplay.receiver_id}
        />
      )}
      {isOpenPayoutMarkFailedConfirmModal && userRole === "superadmin" && payoutToMarkFailed && (
        <PayoutMarkFailedConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadPayoutList}
          isOpen={isOpenPayoutMarkFailedConfirmModal}
          payout={payoutToMarkFailed}
          onClose={closePayoutMarkFailedConfirmModal}
        />
      )}
      <Box className="card mt-3 w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={filterColumns()}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default PayoutList;